<template>
  <div data-test="event-search-bar" class="event-search-bar body--p1--medium">
    <div class="event-search-bar__items">
      <div class="event-search-bar__input-box">
        <div class="event-search-bar__input-box-border"></div>
        <USInputCalendar
          v-model="calendarValue"
          :min-date="new Date()"
          class="event-search-bar__input-item event-search-bar__input-calender"
        >
          <template #heading>
            <p class="display--d2--semibold event-search-bar__input-heading">
              Date
            </p>
          </template>
        </USInputCalendar>
      </div>
      <div class="event-search-bar__input-box">
        <div class="event-search-bar__input-box-border"></div>
        <Suspense>
          <USInputGoogleGeolocation
            v-model:updateCoordinates="coordinatesProp"
            :api-key="apiKey"
            class="event-search-bar__input-item"
            heading="Location"
            :loc="loc"
            @coordinates="updateCoordinates"
          />
        </Suspense>
      </div>
      <div
        class="event-search-bar__input-box event-search-bar__input-box--types"
      >
        <ClientOnly>
          <USInputSelect
            v-model="typeValue"
            class="event-search-bar__input-item event-search-bar__input-select"
            :options="eventTypeSelect"
            :open-arrow="selectArrow"
            :multiselect="true"
            :closed-arrow="selectArrow"
            :select-diameter="22"
            direction="bottom"
          >
            <template #heading>
              <p class="display--d2--semibold event-search-bar__input-heading">
                Type
              </p>
            </template>
          </USInputSelect>
        </ClientOnly>
      </div>
    </div>
    <div class="event-search-bar__lower-items">
      <USButton
        :label="buttonLabel"
        class="event-search-bar__button"
        @click="
          $emit('submitEventSearch', {
            startDate: calendarValue.start
              ? calendarValue.start.toISOString()
              : null,
            endDate: calendarValue.end ? calendarValue.end.toISOString() : null,
            lat: coordinatesProp ? coordinatesProp.lat : null,
            lon: coordinatesProp ? coordinatesProp.lon : null,
            loc: coordinatesProp ? coordinatesProp.loc : null,
            clubs: golfClubsValue ? golfClubsValue : [],
            amenitiesValue: amenitiesValue ? amenitiesValue : [],
            type: typeValue ? typeValue : null
          })
        "
      />
    </div>
  </div>
  <div ref="advancedToggle" class="event-search-bar__advanced-toggle">
    <USIcon
      class="event-search-bar__icon"
      :class="showAdvancedRef ? 'event-search-bar__icon--rotate' : ''"
      name="plus"
      css-diameter="var(--iconDiameter)"
    />
    <USButton
      is="button"
      :label="`Advanced Filters ${
        appliedFilters > 0 ? '(' + appliedFilters + ')' : ''
      }`"
      type="inline"
      @click="toggleAdvancedFilters()"
    />
  </div>
  <div ref="advancedSearch" class="event-search-bar__advanced">
    <div class="event-search-bar__advanced-inputs">
      <div class="event-search-bar__advanced-input-box">
        <ClientOnly>
          <USInputSelect
            v-model="amenitiesValue"
            class="event-search-bar__advanced-input"
            :options="amenitiesTypeSelect"
            :select-diameter="22"
            :multiselect="true"
            direction="bottom"
          >
            <template #heading>
              <p class="display--d2--semibold event-search-bar__input-heading">
                Amenities
              </p>
            </template>
          </USInputSelect>
        </ClientOnly>
      </div>
      <div class="event-search-bar__advanced-input-box">
        <!--
        <ClientOnly>
          <USInputSelect
            v-model="golfClubsValue"
            class="event-search-bar__advanced-input"
            :options="golfClubSelect"
            :select-diameter="22"
            :multiselect="true"
            direction="bottom"
          >
            <template #heading>
              <p class="display--d2--semibold event-search-bar__input-heading">
                Golf Clubs
              </p>
            </template>
          </USInputSelect>
        </ClientOnly>
        -->
      </div>
      <div class="event-search-bar__advanced-buttons">
        <div class="event-search-bar__advanced-buttons--left">
          <USButton
            is="button"
            label="Apply filters"
            type="inline"
            @click="
              $emit('submitEventSearch', {
                startDate: calendarValue.start
                  ? calendarValue.start.toISOString()
                  : null,
                endDate: calendarValue.end
                  ? calendarValue.end.toISOString()
                  : null,
                lat: coordinatesProp ? coordinatesProp.lat : null,
                lon: coordinatesProp ? coordinatesProp.lon : null,
                loc: coordinatesProp ? coordinatesProp.loc : null,
                clubs: golfClubsValue || [],
                amenitiesValue: amenitiesValue || [],
                type: typeValue ? typeValue : null
              })
            "
          />
        </div>
        <div class="event-search-bar__advanced-buttons--right">
          <USButton
            is="button"
            label="Clear all"
            type="inline"
            @click="clearFilters()"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, watch } from 'vue'

import ClientOnly from '@base/components/ClientOnly/ClientOnly.vue'
import USInputCalendar from '@base/components/USInputCalendar/USInputCalendar.vue'
import USInputGoogleGeolocation from '@base/components/USInputGoogleGeolocation/USInputGoogleGeolocation.vue'
import USInputSelect from '@base/components/USInputSelect/USInputSelect.vue'
import USButton from '@base/components/USButton/USButton.vue'
import USIcon from '@base/components/USIcon/USIcon.vue'

interface option {
  label: string
  value: string
}

export default defineComponent({
  name: 'EventSearchBar',
  components: {
    ClientOnly,
    USInputCalendar,
    USInputGoogleGeolocation,
    USInputSelect,
    USButton,
    USIcon
  },
  props: {
    apiKey: {
      type: String,
      default: ''
    },
    eventTypeSelect: {
      type: Array as PropType<option[]>,
      default: () => []
    },
    amenitiesTypeSelect: {
      type: Array as PropType<option[]>,
      default: () => []
    },
    golfClubSelect: {
      type: Array as PropType<option[]>,
      default: () => []
    },
    selectArrow: {
      type: String,
      default: 'trophy'
    },
    buttonLabel: {
      type: String,
      default: ''
    },
    eventType: {
      type: Array as PropType<string[]>,
      default: () => undefined
    },
    startDate: {
      type: String,
      default: ''
    },
    endDate: {
      type: String,
      default: ''
    },
    lat: {
      type: Number,
      default: () => undefined
    },
    lon: {
      type: Number,
      default: () => undefined
    },
    loc: {
      type: String,
      default: () => undefined
    },
    clubs: {
      type: Array as PropType<string[]>,
      default: () => undefined
    },
    amenities: {
      type: Array as PropType<string[]>,
      default: () => undefined
    },
    showAdvanced: {
      type: Boolean,
      default: false
    }
  },
  emits: ['submitEventSearch'],
  setup(props, { emit }) {
    const showAdvancedRef = ref<boolean>(props.showAdvanced)
    const calendarValue = ref(
      props.startDate && props.endDate
        ? {
            start: new Date(props.startDate),
            end: new Date(props.endDate)
          }
        : {}
    )
    const typeValue = ref(props.eventType)
    const golfClubsValue = ref(props.clubs)
    const amenitiesValue = ref(props.amenities)

    const appliedFilters = ref<number>(0)
    const dateFilled = ref<number>(0)
    const locationFilled = ref<number>(0)
    const typeFilled = ref<number>(0)
    const amenitiesFilled = ref<number>(0)
    const clubsFilled = ref<number>(0)

    if (calendarValue.value.start || calendarValue.value.end) {
      dateFilled.value = 1
    }

    if (props.lat || props.loc || props.lon) {
      locationFilled.value = 1
    }

    if (typeValue.value?.length) {
      typeFilled.value = 1
    }

    if (amenitiesValue.value?.length) {
      amenitiesFilled.value = 1
    }

    if (golfClubsValue.value?.length) {
      clubsFilled.value = 1
    }

    const updateAppliedFilter = () => {
      appliedFilters.value =
        dateFilled.value +
        locationFilled.value +
        amenitiesFilled.value +
        clubsFilled.value +
        typeFilled.value
    }

    updateAppliedFilter()

    const coordinatesProp = ref<{
      lat: number
      lon: number
      loc: string
    } | null>(null)
    if (props.lat !== undefined && props.lon !== undefined && props.loc)
      coordinatesProp.value = {
        lat: props.lat,
        lon: props.lon,
        loc: props.loc
      }

    const updateCoordinates = (value: {
      lat: number
      lon: number
      loc: string
    }) => {
      coordinatesProp.value = value
    }

    const advancedSearch = ref<HTMLElement>()
    const advancedToggle = ref<HTMLElement>()

    const clearFilters = () => {
      typeValue.value = []
      golfClubsValue.value = []
      amenitiesValue.value = []
      calendarValue.value

      appliedFilters.value = 0
      dateFilled.value = 0
      locationFilled.value = 0
      amenitiesFilled.value = 0
      clubsFilled.value = 0
      typeFilled.value = 0

      emit('submitEventSearch', {
        startDate: null,
        endDate: null,
        lat: null,
        lon: null,
        loc: null,
        clubs: [],
        amenitiesValue: [],
        type: null
      })
    }

    const toggleAdvancedFilters = () => {
      showAdvancedRef.value = !showAdvancedRef.value
      if (advancedSearch.value) {
        if (
          advancedSearch.value.classList.contains(
            'event-search-bar__advanced--active'
          )
        ) {
          advancedSearch.value.classList.remove(
            'event-search-bar__advanced--active'
          )

          if (advancedToggle.value) {
            advancedToggle.value.classList.remove(
              'event-search-bar__advanced-toggle--hide'
            )
          }
        } else {
          advancedSearch.value.classList.add(
            'event-search-bar__advanced--active'
          )
          if (
            advancedToggle.value &&
            advancedToggle.value.classList.contains(
              'event-search-bar__advanced-toggle'
            )
          ) {
            advancedToggle.value.classList.add(
              'event-search-bar__advanced-toggle--hide'
            )
          }
        }
      }
    }

    watch(
      () => calendarValue.value,
      (newValue) => {
        if (newValue.start || newValue.end) {
          dateFilled.value = 1
        } else {
          dateFilled.value = 0
        }
        updateAppliedFilter()
      }
    )

    watch(
      () => coordinatesProp.value,
      (newValue) => {
        if (newValue?.lat || newValue?.lon || newValue?.loc) {
          locationFilled.value = 1
        } else {
          locationFilled.value = 0
        }
        updateAppliedFilter()
      }
    )

    watch(
      () => amenitiesValue.value,
      (newValue) => {
        if (newValue?.length) {
          amenitiesFilled.value = 1
        } else {
          amenitiesFilled.value = 0
        }
        updateAppliedFilter()
      }
    )

    watch(
      () => golfClubsValue.value,
      (newValue) => {
        if (newValue?.length) {
          clubsFilled.value = 1
        } else {
          clubsFilled.value = 0
        }
        updateAppliedFilter()
      }
    )

    watch(
      () => typeValue.value,
      (newValue) => {
        if (newValue?.length) {
          typeFilled.value = 1
        } else {
          typeFilled.value = 0
        }
        updateAppliedFilter()
      }
    )

    return {
      calendarValue,
      typeValue,
      updateCoordinates,
      coordinatesProp,
      toggleAdvancedFilters,
      advancedSearch,
      advancedToggle,
      clearFilters,
      golfClubsValue,
      amenitiesValue,
      showAdvancedRef,
      appliedFilters
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint';
@use '@base/styles/v1.0/scss/foundations/motion';

.event-search-bar {
  align-items: center;
  background: var(--color--neutral-a5);
  border-radius: var(--border-radius--large);
  box-shadow: var(--box-shadow--b1);
  display: flex;
  flex-flow: column;
  padding: var(--spacing--6);

  @include breakpoint.min-width(breakpoint.$breakpoint--desktop) {
    flex-flow: row nowrap;
  }

  &__items {
    color: var(--color--primary-b2);
    display: flex;
    flex-flow: column wrap;
    width: calc(100% - var(--spacing--6));

    @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
      flex-flow: row nowrap;
    }
  }

  &__input-box {
    margin-bottom: var(--spacing--7);
    width: 100%;

    @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
      flex-basis: 33.3%;
      margin-bottom: 0;
      max-width: 33.3%;
      width: 33.3%;
    }
  }

  &__input-select {
    font-weight: var(--font-weight--regular);
  }

  &__input-heading {
    color: var(--color--neutral-a1) !important;
    font-weight: var(--font-weight--medium) !important;
    margin-bottom: var(--spacing--8);
  }

  &__input-calender {
    &:hover {
      :deep(.us-input-border) {
        box-shadow: none;
      }
    }
  }

  &__input-box-border {
    border-right: var(--line-weight--light) solid var(--color--neutral-a4);
    bottom: 0;
    display: none;
    height: 100%;
    position: absolute;
    right: calc(0px + var(--spacing--8));
    width: var(--spacing--8);
    z-index: 2;

    @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
      display: inline-block;
    }
  }

  &__input-icon {
    position: absolute;
    right: 0;
    top: 50%;
  }

  &__lower-items {
    padding-top: var(--spacing--6);

    @include breakpoint.min-width(breakpoint.$breakpoint--desktop) {
      padding-left: var(--spacing--5);
      padding-top: 0;
    }
  }

  &__advanced {
    align-items: center;
    display: flex;
    flex-flow: column;
    max-height: 0;
    opacity: 0;
    overflow: hidden;
    padding: var(--spacing--6) 0;

    &--active {
      opacity: 1;
      max-height: 100%;
      overflow: visible;
    }
  }

  &__advanced-toggle {
    color: var(--color--primary-b1);
    display: flex;
    flex-direction: row;
    justify-content: center;
    overflow: hidden;
    margin-top: var(--spacing--6);
    max-height: 100%;
  }

  &__icon {
    margin-right: var(--spacing--8);
    transform: rotate(0deg);
    transition: transform motion.duration(3) ease-in;
    will-change: transform;

    &--rotate {
      transform: rotate(45deg);
    }
  }

  &__advanced-inputs {
    display: flex;
    flex-flow: column;
    width: 100%;

    @include breakpoint.min-width(breakpoint.$breakpoint--desktop) {
      flex-flow: row wrap;
      justify-content: space-between;
    }
  }

  &__advanced-input-box {
    &:not(:first-child) {
      margin-top: var(--spacing--6);
    }

    @include breakpoint.min-width(breakpoint.$breakpoint--desktop) {
      flex-basis: 50%;
      margin-top: var(--spacing--6);
      max-width: calc(50% - var(--spacing--6));
      width: calc(50% - var(--spacing--6));
    }
  }

  &__advanced-buttons {
    display: flex;
    flex-basis: 100%;
    flex-flow: row nowrap;
    justify-content: center;
    margin-top: var(--spacing--5);

    &--left {
      margin-right: var(--spacing--6);
    }

    &--right {
      margin-left: var(--spacing--6);
    }
  }
}

:deep(.us-input) {
  color: var(--color--neutral-a1) !important;
  font-weight: var(--font-weight--medium) !important;
}

:deep(.input-heading-wrapper) {
  color: var(--color--neutral-a1) !important;
  font-weight: var(--font-weight--medium) !important;
}

:deep(.input-wrapper--placeholder) {
  color: var(--color--primary-b3) !important;
  font-weight: var(--font-weight--regular) !important;
}

//Calender
:deep(.us-input-border) {
  border: none !important;
  border-radius: 0 !important;
  color: var(--color--primary-b3) !important;
  padding: var(--spacing--8) 0 !important;
  padding-right: var(--spacing--4) !important;

  @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
    padding-right: var(--spacing--5) !important;
  }
}

:deep(.us-input--calendar) {
  z-index: 1;
}

:deep(.us-icon--calendar) {
  right: var(--spacing--8) !important;

  @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
    right: var(--spacing--6) !important;
  }
}

:deep(.vc-popover-content-wrapper) {
  right: 0 !important;
  left: 0 !important;

  @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
    right: -4% !important;
    left: auto !important;
  }
}

//Geo
:deep(.us-input-google-geo__input) {
  color: var(--color--primary-b3) !important;
  font-weight: var(--font-weight--regular) !important;
  padding: var(--spacing--8) 0 !important;
  padding-right: var(--spacing--5) !important;
}

:deep(.us-input-google-geo__input::placeholder) {
  color: var(--color--primary-b3) !important;
}

:deep(.us-input-google-geo__current-loc-button-wrapper) {
  @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
    right: var(--spacing--6) !important;
  }
}

// Event type

:deep(.event-search-bar__input-box--types .us-input-select) {
  border: none !important;
  border-radius: 0 !important;
  color: var(--color--primary-b3) !important;
  padding: var(--spacing--8) 0 !important;
  padding-right: var(--spacing--4) !important;
}

:deep(.event-search-bar__input-box--types .select__selected) {
  padding: 0 !important;
}

:deep(.us-input-select .select__close-icon) {
  height: 24px !important;
  right: var(--spacing--9) !important;
  width: 24px !important;

  @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
    right: var(--spacing--7) !important;
  }
}

:deep(.us-input-select .select__open-icon) {
  height: 24px !important;
  right: var(--spacing--9) !important;
  width: 24px !important;

  @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
    right: var(--spacing--7) !important;
  }
}

:deep(.select__options-container) {
  color: var(--color--neutral-a1);
}
</style>
